import React, { useEffect, useRef, useState } from "react";
import { TitleBlock, Field, Input, SelectInput, Note, WarnNote, Actions, PrimaryBtn, SecondaryBtn, TextBtn } from "../../../../auth/v2/pieces";
import { Spinner, Segmented, OptionCard, OutlineBadge, initialsOf, US_STATE_ABBRS } from "../onboardingPieces";
import { searchTeams, fetchOnboardingCandidates, completeOnboarding, requestTeam, previewCoachCode, linkCoachCode } from "../onboardingV2Services";
import { SESSION_CHANGED } from "../../../../auth/v2/sessionBinding";
import searchIcon from "../../../../../assets/brand/onboarding/search.svg";
import { usePhoneTheme } from "../../../../ForBrands/useIsPhone";
import {
  allCodeTeamsLinked, codeProblem, codeRefusal, codeTeamKey, codeTeamLinkArgs, codeTeamMeta, codeTeamTitle, codeWouldAdd, defaultCodeTeam,
  isProgramCode, pickedCodeTeam, programPickNote,
} from "../../../../auth/Onboarding/codeTeams";
import { displayTeamName, listingLabelOf, stripSeasonSuffix } from "../../../../../utils/teamNames";

// On phones (the AuthFrame and Athlete Mode phone theme, below 640px) the
// team steps follow ATHLETE MODE V2 (mobile): fields that shared a fixed
// 342px row share the column instead (09a 1759:1465, 09b 1576:697), the
// "No team matched" state and the manual team card take the phone sizes
// (1579:1054, 1576:745), and the search results scroll with the page rather
// than in a box of their own (09c 1579:1016). 640px and up is unchanged.
//
// Notes of 10/19: a picked team always becomes a binding (a program with no
// competition binds through a provisional CACHE team, D7); a team typed in
// asks its name, gender, sport, level and age group, checks the teams CACHE
// already has first ("Is it one of these?") and lands on the profile; while
// the team feed is down the search says so and shows the teams already on
// CACHE, and a roster that cannot be read is served as saved, or the team
// is kept and the spot picked later. No step shows a server's error text.

const US_STATES = US_STATE_ABBRS;

// A list card: 16px radius, 18px side padding, rows split by a 1px line.
const ListCard = ({ children, className = "", ...rest }) => <div {...rest} className={`rounded-[16px] border border-bm-border bg-white px-[18px] ${className}`}>{children}</div>;

// A small ring in the V2 orange for a row or button whose request is running.
export const BusyRing = () => (
  <span aria-hidden="true" className="inline-block h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-bm-mutedbg border-t-bm-orange" />
);

// `working` marks the row whose request is running: it keeps full opacity
// and trades the chevron for a ring and "Linking...".
const ListRow = ({ onClick, disabled, avatar, title, sub, last, working = false }) => (
  <button type="button" onClick={onClick} disabled={disabled} aria-busy={working || undefined} className={`flex w-full items-center gap-3 py-4 text-left ${working ? "" : "disabled:opacity-50"} ${last ? "" : "border-b border-bm-mutedbg"}`}>
    {avatar && <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-bm-subtle bm-label-xs text-bm-muted">{avatar}</span>}
    <span className="min-w-0 flex-1"><span className="block truncate bm-label-l text-bm-ink">{title}</span>{sub && <span className="block truncate bm-body-s text-bm-muted">{sub}</span>}</span>
    {working
      ? <span className="flex shrink-0 items-center gap-2 bm-label-l text-bm-orange"><BusyRing />Linking...</span>
      : <span className="bm-body-l text-bm-tertiary">›</span>}
  </button>
);

// The name a team row is read by: its display name (a squad's age group,
// no season), else its name.
const titleOf = (t) => displayTeamName(t) || t?.name || "";

// 09 · Enter your team code (the coach code flow). A code can cover several
// teams, such as the 17U, 16U and 15U squads of one program: they share the
// program id, so the picker keys each row by id and league (codeTeams.js),
// at most one row is picked, each row reads its own league and age group,
// and the link sends the picked row's league with its id. No row starts
// picked unless the athlete's own squad is known (defaultCodeTeam), and
// Confirm waits for a pick. The rows work like native radios from the
// keyboard: the group is one tab stop and the arrow keys move the pick. A
// program code (one code for every age group) says so and lists the squads
// of the athlete's gender; each squad answers for itself (a problem, or
// that it is added beside the main team).
export const TeamCodeStep = ({ state, onLinked, onNoCode }) => {
  const phone = usePhoneTheme();
  const [code, setCode] = useState("");
  const [preview, setPreview] = useState(null);
  const [pick, setPick] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  // The link that went through ({ code, key, data }): when the next step does
  // not open, Confirm opens it again without a second link (a team code
  // counts every use).
  const linked = useRef(null);
  const mounted = useRef(true);
  // Each row's button by its key, for the arrow keys to move focus.
  const optionRefs = useRef({});
  useEffect(() => () => { mounted.current = false; }, []);
  const clean = code.trim().toUpperCase();
  const teams = preview?.teams || [];

  const check = async () => {
    if (clean.length < 6) { setError("Enter the six-character code from your coach or from CACHE."); return; }
    setBusy(true); setError("");
    try {
      const data = await previewCoachCode(clean);
      setPreview(data);
      const first = defaultCodeTeam(data.teams || []);
      setPick(first ? codeTeamKey(first) : "");
    } catch (err) {
      setError(codeRefusal(err, "That code is invalid or not active. Check it with your coach or program."));
    } finally { setBusy(false); }
  };
  const chosen = preview ? pickedCodeTeam(teams, pick) : null;
  // A code for several teams links only the row the athlete picked.
  const needsPick = teams.length > 1 && !chosen;
  // What the picked row says (a program code answers per squad).
  const problem = preview && !needsPick ? codeProblem(preview, chosen) : null;
  const wouldAdd = preview ? codeWouldAdd(preview, chosen) : false;
  // The radio group keyboard, as native radios: Tab reaches one row, the
  // picked one, else the first that can still be linked, which it focuses
  // without picking (Confirm keeps waiting). An arrow key moves from the
  // focused row to the next or previous row that can be linked, wrapping
  // at the ends, and picks it. Linked rows are skipped. With Alt, Ctrl or
  // Cmd held the arrow keys stay the browser's (Alt+Left and Cmd+Left go
  // back a page), as native radios and SegmentedControl leave them.
  const pickable = teams.filter((t) => !t.alreadyLinked);
  const tabStop = pickable.find((t) => codeTeamKey(t) === pick) || pickable[0];
  const onPickerKey = (e, focused) => {
    const step = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 }[e.key];
    if (!step || e.altKey || e.ctrlKey || e.metaKey || busy || !pickable.length) return;
    e.preventDefault();
    const at = pickable.findIndex((t) => codeTeamKey(t) === codeTeamKey(focused));
    const next = at < 0
      ? pickable[step > 0 ? 0 : pickable.length - 1]
      : pickable[(at + step + pickable.length) % pickable.length];
    const key = codeTeamKey(next);
    setPick(key);
    optionRefs.current[key]?.focus();
  };
  const confirm = async () => {
    if (needsPick || busy || problem) return;
    setBusy(true); setError("");
    const key = chosen ? codeTeamKey(chosen) : "";
    let data = linked.current && linked.current.code === preview.code && linked.current.key === key ? linked.current.data : null;
    if (!data) {
      try {
        data = await linkCoachCode(preview.code, ...codeTeamLinkArgs(chosen));
        linked.current = { code: preview.code, key, data };
      } catch (err) {
        setError(codeRefusal(err, "We could not link this code."));
        setBusy(false);
        return;
      }
    }
    // Linked. The busy state stays until the next step has opened (the
    // wizard waits on the score refresh); a failure there says so and turns
    // the buttons back on.
    try {
      await onLinked(data);
    } catch {
      if (mounted.current) setError("Your team is linked, but the next step did not open. Try again.");
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  if (preview) {
    const program = isProgramCode(preview);
    // "Pick yours" only when there are squads to pick from.
    const sub = program && teams.length > 1
      ? programPickNote(preview)
      : preview.teamCode ? "This code was issued by CACHE for the team below. Confirming links your profile to it and verifies it." : `Confirming links your profile to ${preview.coachName || preview.coachEmail || "your coach"}'s team and verifies it.`;
    return (
      <>
        <TitleBlock eyebrow={`Code ${preview.code}`} title="Is this your team?" sub={sub} />
        {teams.length > 1 ? (
          <div role="radiogroup" aria-label="Pick your team" className="flex flex-col gap-[10px]">
            {teams.map((t) => {
              const key = codeTeamKey(t);
              const note = t.alreadyLinked ? " · already linked" : t.problem ? " · cannot be linked" : "";
              return (
                <OptionCard
                  key={key}
                  ref={(el) => { optionRefs.current[key] = el; }}
                  role="radio"
                  aria-checked={pick === key}
                  tabIndex={t === tabStop ? 0 : -1}
                  disabled={!!t.alreadyLinked || busy}
                  selected={pick === key}
                  onClick={() => !t.alreadyLinked && setPick(key)}
                  onKeyDown={(e) => onPickerKey(e, t)}
                  title={codeTeamTitle(t)}
                  sub={codeTeamMeta(t) + note}
                />
              );
            })}
          </div>
        ) : (
          <div className="flex items-center gap-3 rounded-[12px] border border-bm-border bg-white p-4">
            <span className="flex h-9 w-9 items-center justify-center rounded-[8px] bg-bm-ink bm-label-xs text-white">{initialsOf(...(chosen?.teamName || "T").split(" "))}</span>
            <div className="min-w-0"><div className="bm-h-s text-bm-ink">{chosen ? codeTeamTitle(chosen) : "Team"}</div><div className="bm-body-m text-bm-muted">{codeTeamMeta(chosen)}</div></div>
          </div>
        )}
        {problem ? <Note tone="critical">{problem}</Note>
          : chosen?.alreadyLinked ? <Note>You are already verified on this team.</Note>
          : needsPick ? <Note>{allCodeTeamsLinked(teams) ? "You are already verified on every team this code covers." : program ? "Pick your age group to continue." : "Pick your team to continue."}</Note>
          : wouldAdd ? <Note>Your profile keeps {preview.yourTeam || "your current team"} as your team. {chosen ? codeTeamTitle(chosen) : "This team"} is added as another roster you play on.</Note>
          : <Note>Confirming verifies your profile and fills in your sport, gender, league and roster.</Note>}
        {error && <Note tone="critical">{error}</Note>}
        <Actions>
          <PrimaryBtn onClick={confirm} loading={busy} disabled={!!problem || needsPick || !!chosen?.alreadyLinked}>{wouldAdd ? "Confirm and add team" : "Confirm and verify"}</PrimaryBtn>
          <SecondaryBtn onClick={() => { setPreview(null); setCode(""); setError(""); linked.current = null; }} disabled={busy}>Use a different code</SecondaryBtn>
        </Actions>
      </>
    );
  }
  return (
    <>
      <TitleBlock eyebrow="Team" title="Enter your team code" sub="Your coach or program shares one code per team. It fills in your sport, gender, league and roster at once." />
      {/* Phones (1759:1422) add the hint under the field. */}
      <Field label="Team code" hint={phone ? "Six characters, not case sensitive" : undefined}>
        <Input className="uppercase tracking-[0.2em]" placeholder="ABC123" maxLength={8} value={code} autoFocus onChange={(e) => { setCode(e.target.value.toUpperCase().slice(0, 8)); if (error) setError(""); }} onKeyDown={(e) => { if (e.key === "Enter") check(); }} error={!!error} />
      </Field>
      <Note>One code covers the whole team, whether your coach shared it or CACHE issued it. You do not need a personal code.</Note>
      {error && <Note tone="critical">{error}</Note>}
      <Actions>
        <PrimaryBtn onClick={check} loading={busy} disabled={clean.length < 6}>Check code</PrimaryBtn>
        <TextBtn onClick={onNoCode}>I do not have a code</TextBtn>
      </Actions>
    </>
  );
};

// The city and State picker of 09a and 09b. Desktop: a 206px city and a
// 126px picker. Phones: the city takes what the 126px picker leaves (the two
// fixed widths overflow a 360px screen). The request needs the state, so the
// picker stays (D-o17).
const CityStateRow = ({ city, onCity, st, onState }) => {
  const phone = usePhoneTheme();
  return (
    <div className={phone ? "grid grid-cols-[minmax(0,1fr)_126px] items-start gap-[10px]" : "flex items-start gap-[10px]"}>
      <Input className={phone ? "" : "!w-[206px]"} placeholder="Las Vegas" value={city} onChange={onCity} aria-label="City" />
      <SelectInput className={phone ? "" : "w-[126px]"} value={st} onChange={onState} aria-label="State">
        <option value="">State</option>
        {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
      </SelectInput>
    </div>
  );
};

// The gender a team is searched and filed for: the athlete's own pick.
const GENDERS = [["F", "Women / Girls"], ["M", "Men / Boys"]];
const GENDER_REQUIRED = "Pick Women / Girls or Men / Boys to continue.";
const genderOf = (value) => (value === "M" || value === "F" ? value : "");

// What the team search says about its own answer (the server's dataStatus):
// limited while the team feed is down (only the teams already on CACHE) or
// answered from a saved search. Copy is CACHE's own, never the server's
// text.
const SEARCH_LIMITED = "Team search is limited right now. Showing teams already on CACHE.";
const SEARCH_SAVED = "Team search is limited right now. Showing saved results.";
const UNAVAILABLE = Object.freeze({ state: "unavailable" });
export const searchLimitText = (status) => {
  const state = status && typeof status === "object" ? status.state : "";
  if (!state || state === "live") return "";
  return state === "stale" ? SEARCH_SAVED : SEARCH_LIMITED;
};
// The dataStatus a searchTeams answer carries (a property of the rows).
const statusOfRows = (rows) => (rows && typeof rows === "object" && rows.dataStatus && typeof rows.dataStatus === "object" ? rows.dataStatus : null);

// 09a · Tell us where you play (no code): gender, team or school, city and
// state. No gender starts picked (a boy who left it on a default searched
// girls' teams): the athlete's own, else a pick is required. A failed
// search opens the search step limited, never with the server's text.
export const TeamDetailsStep = ({ state, onResults, onNoResults }) => {
  const [gender, setGender] = useState(genderOf(state.details?.gender));
  const [name, setName] = useState(state.team?.manualTeam?.name || "");
  const [city, setCity] = useState(state.team?.manualTeam?.city || "");
  const [st, setSt] = useState(state.team?.manualTeam?.state || "");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const draft = { gender, name: name.trim(), city: city.trim(), state: st };
  const search = async () => {
    if (!gender) { setError(GENDER_REQUIRED); return; }
    if (name.trim().length < 2) { setError("Enter your team or school name."); return; }
    setBusy(true); setError("");
    try {
      let rows;
      try {
        rows = await searchTeams(name.trim(), gender);
      } catch (e) {
        // Another tab changed the session: the wizard says so itself.
        if (e?.code === SESSION_CHANGED) return;
        onNoResults(draft, UNAVAILABLE);
        return;
      }
      const list = Array.isArray(rows) ? rows : [];
      if (list.length) onResults(list, draft, statusOfRows(rows)); else onNoResults(draft, statusOfRows(rows));
    } finally { setBusy(false); }
  };
  return (
    <>
      <TitleBlock eyebrow="Team · no code" title="Tell us where you play" sub="Without a code we cannot auto-fill your roster. Add the details and your profile stays unverified until a code exists." />
      <Field label="Gender" hint="This affects how your performance is weighted against the right comparison set.">
        <Segmented value={gender} onChange={(v) => { setGender(v); if (error === GENDER_REQUIRED) setError(""); }} options={GENDERS} label="Gender" />
      </Field>
      <Field label="Team or school name">
        <div className="flex h-14 items-center gap-[10px] rounded-[12px] border border-bm-border bg-white px-[18px] focus-within:border-bm-ink focus-within:shadow-[inset_0_0_0_1px_#2c2c2c]">
          <img src={searchIcon} alt="" className="h-4 w-4" />
          <input className="ob-bare min-w-0 flex-1" placeholder="Desert Pines High" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") search(); }} aria-label="Team or school name" />
        </div>
      </Field>
      <Field label="City and state">
        <CityStateRow city={city} onCity={(e) => setCity(e.target.value)} st={st} onState={(e) => setSt(e.target.value)} />
      </Field>
      <WarnNote badge="Unverified">Your profile works and is visible, but carries an unverified badge until someone from your team creates a code.</WarnNote>
      {error && <Note tone="critical">{error}</Note>}
      <Actions><PrimaryBtn onClick={search} loading={busy}>Continue</PrimaryBtn></Actions>
    </>
  );
};

// A search row's line: its league (no season), its city and the season of
// its latest event ("Latest season 2025-26"), never a year beside the name.
const teamMeta = (t) => [stripSeasonSuffix(t.leagueName || ""), t.city, t.season ? `Latest season ${t.season}` : ""].filter(Boolean).join(" · ");

// The body of a failed request: axios keeps it on response.data, the other
// service helpers on err.data.
const dataOf = (e) => {
  const d = e?.response?.data ?? e?.data;
  return d && typeof d === "object" ? d : {};
};

// A failed request the server could not answer (a 5xx): its body's text is
// never shown, except the neutral sentences CACHE answers with when team
// data is down (code provider_unavailable) or a program's team could not be
// made just now (team_unavailable).
const NEUTRAL_REFUSALS = new Set(["team_unavailable"]);
const serverFailed = (e) => {
  const d = dataOf(e);
  return (e?.response?.status ?? e?.status) >= 500 && d.code !== "provider_unavailable" && !NEUTRAL_REFUSALS.has(d.error);
};

// The server's reason for a failed request, not axios's status line.
const reasonOf = (e, fallback) => {
  if (serverFailed(e)) return fallback;
  const d = dataOf(e);
  return d.message || d.error || (e?.message && !/status code/i.test(e.message) && !/^HTTP \d+/.test(e.message) ? e.message : "") || fallback;
};

// A request the team feed could not answer: code provider_unavailable, a
// 5xx, no answer at all or a time limit. Retry later; its text is never
// shown.
export const feedDown = (e) => {
  const status = e?.response?.status ?? e?.status;
  return dataOf(e).code === "provider_unavailable" || e?.code === "provider_unavailable" || !!e?.timedOut || !status || status >= 500;
};

// The team request answers 409 {error: 'possible_matches', matches} when
// CACHE already has a team with that name. The matches, or null.
export const possibleMatchesOf = (e) => {
  const status = e?.response?.status ?? e?.status;
  const d = dataOf(e);
  return status === 409 && d.error === "possible_matches" && Array.isArray(d.matches) ? d.matches : null;
};

// A team match row (find_existing_teams) in the shape the search step hands
// the roster step: API programs roster through their catalogue key (one
// with none binds through a provisional CACHE team), partner teams through
// their CACHE league, SportsRadar teams through theirs. A row's programOnly
// goes with it: false says its competitions could not be checked, so the
// roster step waits for the feed instead of binding it as a program.
export const teamFromMatch = (m = {}) => {
  const partner = !!m.cacheTeamId || m.source === "cache_teams";
  return {
    id: m.teamId || m.id || "",
    name: m.name || m.teamName || "",
    ...(m.displayName ? { displayName: m.displayName } : {}),
    ...(m.ageGroup ? { ageGroup: m.ageGroup } : {}),
    ...(typeof m.programOnly === "boolean" ? { programOnly: m.programOnly } : {}),
    city: m.city || "",
    state: m.state || "",
    leagueName: m.leagueLabel || m.leagueName || "",
    leagueKey: m.leagueKey || "",
    catalogueKey: partner ? "" : m.catalogueKey || m.league || "",
    cacheTeamId: partner ? m.cacheTeamId || m.teamId || "" : "",
    cacheLeague: partner ? m.cacheLeague || m.league || "" : "",
    teamSource: partner ? "cache_teams" : m.source === "sportsradar" ? "sportsradar" : "cerebro",
  };
};
// Other people asked for this team already (no team of its own yet): how
// many, when the server counts them (requestCount; the asker's own request
// is never counted).
export const isRequestMatch = (m) => m?.source === "team_request";
export const requestedLine = (m) => {
  const n = Number(m?.requestCount) || 0;
  return n > 0 ? `${n} ${n === 1 ? "person" : "people"} asked for this team` : "Already asked for on CACHE";
};
// A match's qualifier: the server's listing label (league or level words,
// never a vendor's name, D16), else its league without the season.
export const matchMeta = (m) => [
  isRequestMatch(m) ? requestedLine(m) : listingLabelOf(m),
  [m.city, m.state].filter(Boolean).join(", "),
].filter(Boolean).join(" · ");

// "No team matched". Phones (EmptyState 1579:1054): a 12px radius dashed
// card, a 36px circle, the title in bm-h-s, bm-body-m text and a 40px
// button. The web keeps its "Clear search" label (there are no filters).
// While the search is limited the card says so, with no button of its own
// (the step's Try again is below it).
const NoTeamMatched = ({ phone, title = "No team matched", text, onClear }) => (phone ? (
  <div className="flex flex-col items-center gap-2 rounded-[12px] border border-dashed border-bm-border bg-bm-subtle px-6 py-7 text-center">
    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-bm-mutedbg"><img src={searchIcon} alt="" className="h-4 w-4 opacity-60" /></span>
    <span className="bm-h-s text-bm-ink">{title}</span>
    <span className="bm-body-m text-bm-muted">{text}</span>
    {onClear && <button type="button" onClick={onClear} className="flex h-10 items-center rounded-full bg-bm-ink px-[18px] bm-label-l text-white">Clear search</button>}
  </div>
) : (
  <div className="flex flex-col items-center gap-[14px] rounded-[24px] border-[1.5px] border-dashed border-bm-strong bg-bm-subtle px-6 py-9 text-center">
    <span className="flex h-[56px] w-[56px] items-center justify-center rounded-full bg-bm-mutedbg"><img src={searchIcon} alt="" className="h-5 w-5 opacity-60" /></span>
    <span className="bm-label-l text-bm-ink">{title}</span>
    <span className="bm-body-s text-bm-muted">{text}</span>
    {onClear && <button type="button" onClick={onClear} className="flex h-9 items-center rounded-full bg-bm-ink px-4 bm-label-xs text-white">Clear search</button>}
  </div>
));

// 09c · Where do you play? Search results, or "No team matched".
// mode "add" is the edit hub's "Add a team": same search, no manual path;
// `hasMain` false says the first team added becomes the main team. While
// the team feed is down (`initialStatus`, or the answer's dataStatus, or a
// failed search) the step says the search is limited, offers Try again and
// keeps "Add it yourself" behind a warning that the team may already exist.
export const TeamSearchStep = ({ initialQuery = "", initialRows = null, initialStatus = null, gender, onPick, onManual, mode = "onboarding", onCancel, hasMain = true }) => {
  const adding = mode === "add";
  const phone = usePhoneTheme();
  const [q, setQ] = useState(initialQuery);
  const [rows, setRows] = useState(initialRows);
  const [status, setStatus] = useState(initialStatus || statusOfRows(initialRows));
  const [busy, setBusy] = useState(false);
  const run = async (query) => {
    if ((query || "").trim().length < 2) return;
    setBusy(true);
    try {
      const found = await searchTeams(query.trim(), gender);
      setRows(Array.isArray(found) ? found : []);
      setStatus(statusOfRows(found));
    } catch {
      // A failed search is a limited one, with Try again: never "No team
      // matched", which sent athletes to add teams that exist.
      setRows([]);
      setStatus(UNAVAILABLE);
    } finally { setBusy(false); }
  };
  // Only on mount: a fresh search when the details step handed over a query.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (initialRows === null && initialQuery) run(initialQuery); }, []);
  const limited = searchLimitText(status);
  const none = rows !== null && rows.length === 0 && !busy;
  const shown = rows ? rows.slice(0, 60) : [];
  const retry = () => run(q.trim() ? q : initialQuery);
  return (
    <>
      {adding
        ? <TitleBlock eyebrow="Teams" title="Add a team" sub={hasMain ? "Search for another team you play on, such as your club or your school team. Its games count toward your CacheScore." : "Search for your team. The first team you add becomes your main team."} />
        : <TitleBlock eyebrow="Team · required" title="Where do you play?" sub="Add at least one team. This is what your performance score is built on." />}
      <div className="flex h-14 items-center gap-[10px] rounded-[12px] border border-bm-border bg-white px-[18px] focus-within:border-bm-ink focus-within:shadow-[inset_0_0_0_1px_#2c2c2c]">
        <img src={searchIcon} alt="" className="h-4 w-4" />
        <input className="ob-bare min-w-0 flex-1" placeholder="Search your team or school" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") run(q); }} aria-label="Search your team or school" />
      </div>
      {limited && !busy && <Note>{limited}</Note>}
      {busy && <div className="flex justify-center py-6"><Spinner /></div>}
      {!busy && shown.length > 0 && (
        <ListCard className={phone ? "" : "max-h-[420px] overflow-y-auto"}>
          {shown.map((t, i) => <ListRow key={`${t.cacheTeamId || t.id}|${t.leagueKey || t.cacheLeague || ""}`} onClick={() => onPick(t)} title={titleOf(t)} sub={teamMeta(t)} last={i === shown.length - 1} />)}
        </ListCard>
      )}
      {none && (limited
        ? <NoTeamMatched phone={phone} title="Search is limited right now" text="Your team may already be on CACHE. Try again in a minute." />
        : <NoTeamMatched phone={phone} text={adding ? "We could not find it in our roster data. Try another name, or ask your coach for a team code." : "We could not find it in our roster data. Add it yourself and verify later."} onClear={() => { setQ(""); setRows(null); }} />)}
      {adding ? (
        <Actions>
          <PrimaryBtn onClick={() => run(q)} disabled={q.trim().length < 2}>{limited ? "Try again" : "Search"}</PrimaryBtn>
          {onCancel && <TextBtn onClick={onCancel}>Cancel</TextBtn>}
        </Actions>
      ) : limited ? (
        <Actions>
          <PrimaryBtn onClick={retry} disabled={busy}>Try again</PrimaryBtn>
          <TextBtn onClick={() => onManual(q)}>Add it yourself anyway</TextBtn>
          <p className="text-center bm-body-s text-bm-tertiary">Your team may already be on CACHE. Added now, it is checked against the team list later.</p>
        </Actions>
      ) : (
        <Actions>
          <PrimaryBtn onClick={() => (none || !rows ? onManual(q) : run(q))}>{none || !rows ? "Add it manually" : "Search"}</PrimaryBtn>
          {shown.length > 0 && <TextBtn onClick={() => onManual(q)}>My team is not listed</TextBtn>}
          <p className="text-center bm-body-s text-bm-tertiary">No coach code needed. An unverified team never blocks your profile.</p>
        </Actions>
      )}
    </>
  );
};

// Why "Add a team" refused, when the server sent no sentence of its own.
const ADD_REFUSALS = {
  no_primary: "Add your main team first. A team code from your coach sets it.",
  already_listed: "This team is already on your profile.",
  too_many_teams: "You can list up to 4 teams. Remove one to add another.",
  sport_mismatch: "This team plays a different sport from your main team.",
  gender_mismatch: "This team is in a different division from your main team.",
  name_mismatch: "The name on that roster spot does not match your account.",
  player_not_on_roster: "That player is not on this team's roster.",
  roster_unavailable: "This roster is not available right now. Try again later.",
  profile_taken: "That roster spot is already claimed. Pick another or add the team without one.",
};

// The team's own sport key when its league key says it: a partner league
// ends in its sport ("cache_<org>_<age>_wbb"), a competition key names its
// gender ("..._girls_..."). "" when it does not.
export const teamSportKey = (team = {}) => {
  const key = String(team.cacheLeague || team.catalogueKey || "").toLowerCase();
  const sport = key.match(/(?:^|_)(mbb|wbb)$/);
  if (sport) return sport[1];
  if (/(?:^|_)(girls|women|womens)(?:_|$)/.test(key)) return "wbb";
  if (/(?:^|_)(boys|men|mens)(?:_|$)/.test(key)) return "mbb";
  return "";
};

// The sport key onboarding completion files the team under: men's or
// women's basketball by the athlete's own gender; with no gender on file the
// team's own key, else none, and the server reads the team's league (Kobi's
// 10/7 follow-ups: an unknown gender was sent as women's basketball).
export const identitySportKey = (gender, team) => (gender === "M" ? "mbb" : gender === "F" ? "wbb" : teamSportKey(team));

// "Oct 12, 2026" for a saved roster's time, "" when it cannot be read.
const savedOn = (iso) => {
  const when = iso ? new Date(iso) : null;
  return when && !Number.isNaN(when.getTime()) ? when.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";
};

// Is this you? The roster match for the picked team.
// mode "add" is the edit hub's "Add a team" (and the Club and Team fields'
// pick): the pick goes to onAdd as another team (or the main team, when
// there is none), never onboarding/complete. onDone gets the answer.
//
// A program with no competition on file (no league) has no roster: it binds
// like any team, through a provisional CACHE team the server makes (D7),
// with "Add this team". A row whose competitions could not be checked just
// now (programOnly false) waits for the feed. A roster the feed cannot give
// is served as saved ("Roster saved <date>"), else the athlete can try
// again or keep the team and pick their spot later (pendingRosterPick).
// `listed` (add mode): the team is on the profile already, kept while its
// roster could not be read ("Pick your spot"), so only a roster row can be
// picked: nothing offers to add or keep it again, and onNotMe cancels.
//
// A pick can take a while (the save, then the score refresh the wizard waits
// on), so from the click until the next step opens the rows and buttons are
// off, the picked row reads "Linking..." with a ring, and a status line says
// what is happening; a failure shows its reason and turns them back on.
export const IdentityStep = ({ state, team, onDone, onNotMe, mode = "onboarding", onAdd, listed = false }) => {
  const adding = mode === "add";
  const spotOnly = adding && listed;
  const [candidates, setCandidates] = useState(null);
  // How the roster was read: {status 'live' | 'saved', savedAt}.
  const [roster, setRoster] = useState({ status: "live", savedAt: null });
  // Why the roster did not load: "down" (the feed) or "failed" (anything else).
  const [loadError, setLoadError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  // The playerId being linked ("" for "keep the team"), null when idle.
  const [working, setWorking] = useState(null);
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);
  const league = team.cacheLeague || team.catalogueKey || "";
  const teamId = team.cacheTeamId || team.id;
  const source = team.teamSource || (team.cacheTeamId ? "cache_teams" : "cerebro");
  const programOnly = !league && team.programOnly !== false;
  const unresolved = !league && team.programOnly === false;
  const name = titleOf(team) || "the team";
  useEffect(() => {
    let cancelled = false;
    if (!league) {
      setCandidates([]);
      return undefined;
    }
    setCandidates(null);
    setLoadError("");
    (async () => {
      try {
        const data = await fetchOnboardingCandidates({ league, teamId, firstName: state.firstName, lastName: state.lastName });
        if (cancelled) return;
        setCandidates(data?.candidates || []);
        setRoster({ status: data?.rosterStatus === "saved" ? "saved" : "live", savedAt: data?.savedAt || null });
      } catch (e) {
        if (cancelled) return;
        setCandidates([]);
        setLoadError(feedDown(e) ? "down" : "failed");
      }
    })();
    return () => { cancelled = true; };
  }, [league, teamId, state.firstName, state.lastName, attempt]);

  // `pending`: keep the team and pick the spot later (the roster could not
  // be read).
  const finish = async (playerId, pending = false) => {
    if (busy) return;
    setBusy(true); setWorking(playerId || ""); setError("");
    const extra = { ...(programOnly ? { programOnly: true } : {}), ...(pending ? { pendingRosterPick: true } : {}) };
    const leagueLabel = stripSeasonSuffix(team.leagueName || "");
    let out;
    try {
      if (adding) {
        out = await onAdd({ league, teamId, teamSource: source, teamName: team.name, leagueLabel, playerId: playerId || "", ...extra });
      } else {
        const gender = genderOf(state.details?.gender);
        const sport = identitySportKey(gender, team);
        out = await completeOnboarding({
          sport, league, teamId, teamSource: source, currentTeam: team.name, playerId: playerId || "", cascade: { leagueDisplayName: leagueLabel },
          ...extra,
          // A program with no competition is made into a CACHE team from
          // what the athlete told us.
          ...(programOnly ? { gender, city: team.city || "", state: team.state || "" } : {}),
        });
      }
    } catch (e) {
      const d = dataOf(e);
      const code = d.error || "";
      if (adding) setError(serverFailed(e) ? reasonOf(e, "We could not add this team.") : d.message || ADD_REFUSALS[code] || reasonOf(e, "We could not add this team."));
      else {
        setError(code === "profile_taken" ? "That roster spot is already claimed. Pick another or continue without one."
          : ["name_mismatch", "player_not_on_roster", "roster_unavailable"].includes(code) ? (d.message || "That spot cannot be claimed.")
            : reasonOf(e, "We could not save your team."));
      }
      setBusy(false); setWorking(null);
      return;
    }
    // Saved. The busy state stays until the next step has opened (onDone
    // waits on the score refresh), so nothing looks idle meanwhile.
    try {
      await onDone(out);
    } catch (e) {
      // A request that ran out of time (onboardingV2Services) says nothing
      // more useful than this.
      const saved = "Your team is saved, but the next step did not open. Try again.";
      if (mounted.current) setError(e?.timedOut ? saved : reasonOf(e, saved));
    } finally {
      if (mounted.current) { setBusy(false); setWorking(null); }
    }
  };
  const shown = (candidates || []).slice(0, 12);
  const sub = spotOnly
    ? `Pick yourself on the ${name} roster to link your spot on it. Only the row with your name can be picked.`
    : programOnly
      ? `${name} has no roster on file yet. ${adding ? "Add it to your teams" : "Add it as your team"}, and a coach can confirm it later.`
      : unresolved
        ? `We could not check ${name}'s league just now.`
        : adding
          ? `Pick yourself on the ${name} roster to link your spot on it. Its games count toward your CacheScore. Only the row with your name can be picked. If you are not listed, add the team without a roster spot and your coach can confirm it.`
          : `Pick yourself on the ${name} roster and your game data links to your profile. Only the row with your name can be picked; if you are not listed, keep the team and your coach can add you.`;
  const saved = savedOn(roster.savedAt);

  let body;
  if (programOnly) body = <Note>There is no roster on file for this team yet, so there is no spot to pick. It shows as unverified until a coach confirms it.</Note>;
  else if (unresolved) body = <Note tone="critical">Its roster is not available right now. Try again in a minute, or pick another team.</Note>;
  else if (candidates === null) body = <div className="flex justify-center py-6"><Spinner /></div>;
  else if (loadError === "down") body = <Note>{spotOnly ? "The roster is not available right now. Try again in a minute. Your team stays on your profile." : "The roster is not available right now. Try again in a minute, or keep the team and pick your spot later."}</Note>;
  else if (loadError) body = <Note tone="critical">{spotOnly ? "We could not load this roster. Try again later." : "We could not load this roster. Pick a different team."}</Note>;
  else if (shown.length === 0) body = <Note>{spotOnly ? "We did not find a matching player on this roster. Your team stays on your profile." : adding ? "We did not find a matching player on this roster. You can still add the team." : "We did not find a matching player on this roster. Continue and add your stats yourself."}</Note>;
  else {
    body = (
      <>
        {roster.status === "saved" && <Note>{saved ? `Roster saved ${saved}.` : "Roster saved earlier."} The live roster is not available right now.</Note>}
        <ListCard aria-busy={busy || undefined}>
          {shown.map((c, i) => {
            // claimable comes from the server: the row's name matches the
            // account name (exact or 90%+) and nobody with that name holds it.
            // mine: the account already holds this row.
            const blocked = c.claimable === false;
            const why = c.mine ? (blocked ? " · your spot, but the name does not match your account" : " · your spot")
              : c.takeover ? " · claimed under another name, you can take it" : c.claimed ? " · already claimed" : blocked ? " · name does not match your account" : "";
            return (
              <ListRow key={c.playerId} working={working === c.playerId} disabled={busy || blocked} onClick={() => finish(c.playerId)} avatar={initialsOf(c.firstName, c.lastName)} title={c.fullName || `${c.firstName} ${c.lastName}`} sub={[c.position, c.jersey ? `#${c.jersey}` : "", c.status].filter(Boolean).join(" · ") + why} last={i === shown.length - 1} />
            );
          })}
        </ListCard>
      </>
    );
  }

  // A listed team (spotOnly) is kept as it is: only its Try again stays.
  let keep = null;
  if (spotOnly) {
    if (loadError === "down") keep = <SecondaryBtn onClick={() => setAttempt((n) => n + 1)} disabled={busy}>Try again</SecondaryBtn>;
  } else if (programOnly) keep = <PrimaryBtn onClick={() => finish("")} loading={busy}>Add this team</PrimaryBtn>;
  else if (loadError === "down") {
    keep = (
      <>
        <SecondaryBtn onClick={() => setAttempt((n) => n + 1)} disabled={busy}>Try again</SecondaryBtn>
        <SecondaryBtn onClick={() => finish("", true)} disabled={busy}>{busy && working === "" ? (adding ? "Adding..." : "Saving...") : "Keep the team and pick my spot later"}</SecondaryBtn>
      </>
    );
  } else if (!unresolved && !loadError) {
    keep = adding
      ? <SecondaryBtn onClick={() => finish("")} disabled={busy}>{busy && working === "" ? "Adding..." : "Add the team without a roster spot"}</SecondaryBtn>
      : <SecondaryBtn onClick={() => finish("")} disabled={busy}>{busy && working === "" ? "Saving..." : "I am not on this list, keep the team"}</SecondaryBtn>;
  }

  return (
    <>
      <TitleBlock eyebrow={adding ? "Teams" : "Team"} title="Is this you?" sub={sub} />
      {body}
      {busy && (
        <p role="status" className="flex items-center gap-2 bm-body-m text-bm-muted">
          <BusyRing />
          {adding && !spotOnly ? `Adding ${name}...` : working ? "Linking your roster spot. This can take a few seconds." : `Saving ${name}. This can take a few seconds.`}
        </p>
      )}
      {error && <div role="alert"><Note tone="critical">{error}</Note></div>}
      <Actions>
        {keep}
        <TextBtn onClick={onNotMe} disabled={busy}>{spotOnly ? "Cancel" : "Pick a different team"}</TextBtn>
      </Actions>
    </>
  );
};

// The request's sport key: basketball is mbb or wbb by the athlete's
// gender, the way every other team record stores it; with no gender the
// sport goes as named and the server guesses no gender (an unknown gender
// was once sent as women's basketball). Any other sport goes as named.
export const requestSportKey = (sport, gender) => {
  if (String(sport || "").trim().toLowerCase() !== "basketball") return sport;
  return gender === "M" ? "mbb" : gender === "F" ? "wbb" : sport;
};

// The levels a team plays at (the server's words) and the tier key the
// request stores beside them.
const HS = "HS / Grassroots";
// An 8th grade and under team (the New Balance Jr. P32, a middle school):
// sent as an HS / Grassroots club of 14U and under, so the server files it
// in the Club field and labels it Junior (team_kinds.is_junior).
export const JUNIOR = "Junior (8th grade and under)";
const LEVELS = [[HS, "hs"], [JUNIOR, "junior"], ["College", "college"], ["Pro", "pro"]];
// The age groups of an HS / Grassroots club ("Oakland Soldiers 16U").
const AGE_GROUPS = ["17U", "16U", "15U", "14U", "13U", "12U", "11U", "10U"];
const JUNIOR_AGE_GROUPS = ["14U", "13U", "12U", "11U", "10U"];
// The level the server reads for a level picked here.
export const serverLevel = (level) => (level === JUNIOR ? HS : level);
// CACHE's sports when the codes list is not loaded.
const DEFAULT_SPORTS = ["Basketball", "Football"];

// 09b · Add your team manually: name, gender, city and state, sport (the
// CACHE sports), level of play and, for an HS / Grassroots club, its age
// group. Before anything is created the server checks the teams CACHE
// already has; when it finds some it answers with them ("Is it one of
// these?"), and the athlete picks one (onPick, the roster step), joins a
// team others already asked for, or confirms that none of them is the
// team. Then the team is on the athlete's profile (a provisional CACHE
// team): onSaved(manual, answer) gets the request's answer ({team, teams,
// fillNote} when it was made), or null when it could not be recorded.
// `sports` is the codes list's sports ({value, name}). `onGender` hears the
// gender picked here just before a match opens its roster step: the matches
// are that gender's, so the team is filed under it, not under the one the
// details step had.
export const TeamManualStep = ({ draft = {}, sports = [], onSaved, onPick, onGender }) => {
  const phone = usePhoneTheme();
  const sportNames = (Array.isArray(sports) && sports.length ? sports.map((s) => s?.name) : DEFAULT_SPORTS).filter(Boolean);
  const [name, setName] = useState(draft.name || "");
  const [city, setCity] = useState(draft.city || "");
  const [st, setSt] = useState(draft.state || "");
  const [gender, setGender] = useState(genderOf(draft.gender));
  const [sport, setSport] = useState(sportNames.includes("Basketball") ? "Basketball" : sportNames[0] || "Basketball");
  const [level, setLevel] = useState(HS);
  const [ageGroup, setAgeGroup] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [matches, setMatches] = useState(null);
  const club = (level === HS || level === JUNIOR) && !!ageGroup;
  // A junior team always has its age group (14U by default).
  const pickLevel = (next) => {
    setLevel(next);
    if (next === JUNIOR) setAgeGroup((a) => (JUNIOR_AGE_GROUPS.includes(a) ? a : JUNIOR_AGE_GROUPS[0]));
    else if (next !== HS) setAgeGroup("");
  };
  // A program the search found without a roster (an older draft): sent with
  // the request while the name is still that program's.
  const programId = draft.cerebroProgramId && name.trim() === String(draft.name || "").trim() ? draft.cerebroProgramId : "";
  // `asName`: the name of a team others already asked for, which this one
  // joins.
  const save = async (confirmed = false, asName = "") => {
    if (busy) return;
    const teamName = (asName || name).trim();
    if (teamName.length < 2) { setError("Enter your team or school name."); return; }
    if (!gender) { setError(GENDER_REQUIRED); return; }
    setBusy(true); setError("");
    try {
      const tier = (LEVELS.find(([label]) => label === level) || LEVELS[0])[1];
      const sent = serverLevel(level);
      const manual = { name: teamName, city: city.trim(), state: st, sport, gender, level: sent, ...(club ? { ageGroup } : {}) };
      const send = (sure) => requestTeam({
        teamName, city: manual.city, state: manual.state, sport: requestSportKey(sport, gender), gender, level: sent, tier,
        ...(club ? { ageGroup } : {}),
        cascade: { sportLabel: sport, tierLabel: level === JUNIOR ? "Junior" : level },
        ...(programId ? { cerebroProgramId: programId } : {}),
        ...(sure ? { confirmedNoMatch: true } : {}),
      });
      let answer = null;
      try {
        answer = await send(confirmed);
      } catch (e) {
        const found = possibleMatchesOf(e);
        if (found) {
          // The program the athlete already looked at is not news to them.
          const others = found.filter((m) => !programId || String(m.teamId || m.id) !== String(programId));
          if (others.length) { setMatches(others); return; }
          try { answer = await send(true); } catch { /* the manual team still saves */ }
        }
        /* any other failure: the manual team still saves */
      }
      await onSaved(manual, answer || null);
    } catch (e) { setError(e?.message || "We could not add your team."); } finally { setBusy(false); }
  };
  // A team from the matches: its roster step, under the gender picked here.
  const pickMatch = (t) => {
    if (onGender && gender) onGender(gender);
    onPick(t);
  };
  if (matches) {
    return (
      <>
        <TitleBlock eyebrow="Team · not listed" title="Is it one of these?" sub={`We found teams named like ${name.trim()}. Pick yours to link its roster, or add your team if none of these is it.`} />
        <ListCard>
          {matches.map((m, i) => {
            const asked = isRequestMatch(m);
            const t = teamFromMatch(m);
            return (
              <ListRow
                key={`${m.cacheTeamId || m.teamId || m.id || m.name}|${m.leagueKey || m.league || i}`}
                disabled={busy || (!asked && !onPick)}
                onClick={() => (asked ? save(true, m.name || "") : pickMatch(t))}
                title={titleOf(t) || m.name}
                sub={matchMeta(m)}
                last={i === matches.length - 1}
              />
            );
          })}
        </ListCard>
        {busy && <p role="status" className="flex items-center gap-2 bm-body-m text-bm-muted"><BusyRing />Adding {name.trim()}. This can take a few seconds.</p>}
        {error && <div role="alert"><Note tone="critical">{error}</Note></div>}
        <Actions>
          <PrimaryBtn onClick={() => save(true)} loading={busy}>None of these, add my team</PrimaryBtn>
          <TextBtn onClick={() => setMatches(null)} disabled={busy}>Change the team name</TextBtn>
        </Actions>
      </>
    );
  }
  const shownName = displayTeamName({ teamName: name.trim(), ageGroup: club ? ageGroup : "" }) || name.trim();
  return (
    <>
      <TitleBlock eyebrow="Team · not listed" title="Add your team manually" sub="We could not find it in our roster data. Add it yourself. Your profile stays live and can be verified later." />
      <Field label="Team or school name"><Input value={name} placeholder="Desert Pines High" onChange={(e) => setName(e.target.value)} aria-label="Team or school name" /></Field>
      <Field label="Gender">
        <Segmented value={gender} onChange={(v) => { setGender(v); if (error === GENDER_REQUIRED) setError(""); }} options={GENDERS} label="Gender" />
      </Field>
      <Field label="City and state">
        <CityStateRow city={city} onCity={(e) => setCity(e.target.value)} st={st} onState={(e) => setSt(e.target.value)} />
      </Field>
      <Field label="Sport">
        <SelectInput value={sport} onChange={(e) => setSport(e.target.value)} aria-label="Sport">
          {sportNames.map((s) => <option key={s}>{s}</option>)}
        </SelectInput>
      </Field>
      <Field label="Level of play">
        <SelectInput value={level} onChange={(e) => pickLevel(e.target.value)} aria-label="Level of play">
          {LEVELS.map(([label]) => <option key={label}>{label}</option>)}
        </SelectInput>
      </Field>
      {level === HS && (
        <Field label="Age group" hint="For a club or travel team. Leave it blank for a school team.">
          <SelectInput value={ageGroup} onChange={(e) => setAgeGroup(e.target.value)} aria-label="Age group">
            <option value="">No age group</option>
            {AGE_GROUPS.map((a) => <option key={a} value={a}>{a}</option>)}
          </SelectInput>
        </Field>
      )}
      {level === JUNIOR && (
        <Field label="Age group" hint="For a middle school or 8th grade team, pick the age group it plays in.">
          <SelectInput value={ageGroup} onChange={(e) => setAgeGroup(e.target.value)} aria-label="Age group">
            {JUNIOR_AGE_GROUPS.map((a) => <option key={a} value={a}>{a}</option>)}
          </SelectInput>
        </Field>
      )}
      {/* The team as it will show, updated while typing. Phones (TeamRow
          1576:745): a 40px crest, the name in bm-h-s, bm-body-m meta. */}
      {name.trim() && (
        <div className={phone ? "flex items-center gap-[14px] rounded-[12px] border border-bm-border bg-white px-4 py-[14px]" : "flex items-center gap-3 rounded-[12px] border border-bm-border bg-white p-4"}>
          <span className={phone ? "flex h-10 w-10 shrink-0 items-center justify-center rounded-[8px] bg-bm-ink bm-label-l text-white" : "flex h-9 w-9 shrink-0 items-center justify-center rounded-[8px] bg-bm-ink bm-label-xs text-white"}>{initialsOf(...name.trim().split(" "))}</span>
          <div className="flex min-w-0 flex-col gap-1">
            <div className={phone ? "bm-h-s text-bm-ink" : "bm-label-l text-bm-ink"}>{shownName}</div>
            <div className={phone ? "flex flex-wrap items-center gap-2 bm-body-m text-bm-muted" : "flex flex-wrap items-center gap-2 bm-body-s text-bm-muted"}>{[[city.trim(), st].filter(Boolean).join(", "), sport, level].filter(Boolean).join(" · ")}<OutlineBadge dot="yellow">Unverified</OutlineBadge></div>
          </div>
        </div>
      )}
      <Note>Unverified teams show a badge on your profile. A coach can confirm it at any time and the badge clears.</Note>
      {busy && <p role="status" className="flex items-center gap-2 bm-body-m text-bm-muted"><BusyRing />Adding {name.trim()}. This can take a few seconds.</p>}
      {error && <div role="alert"><Note tone="critical">{error}</Note></div>}
      <Actions><PrimaryBtn onClick={() => save(false)} loading={busy}>Add team and continue</PrimaryBtn></Actions>
    </>
  );
};

// After the team step: a profile from before this account (a pilot record)
// that looks like the athlete's. Only public details are shown. "This is
// me" asks an admin to review the pair; nothing is joined or taken over
// here. "Not me" (or Continue after asking) carries on with the wizard.
export const EarlierProfileStep = ({ candidates = [], onClaim, onContinue }) => {
  const [asked, setAsked] = useState("");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [leaving, setLeaving] = useState(false);
  // Moving on can take a moment (the score refresh): one click is enough.
  const next = async () => {
    if (leaving) return;
    setLeaving(true);
    try { await onContinue(); } finally { setLeaving(false); }
  };
  const claim = async (id) => {
    setBusy(id); setError("");
    try {
      await onClaim(id);
      setAsked(id);
    } catch (e) {
      setError(reasonOf(e, "We could not send that. Try again, or continue and contact us later."));
    } finally { setBusy(""); }
  };
  if (asked) {
    return (
      <>
        <TitleBlock eyebrow="Team" title="Thanks, we will take a look" sub="An admin will review the earlier profile and join it to this one if it is yours. You do not need to do anything else." />
        <Note tone="success">Your request was sent for review.</Note>
        <Actions><PrimaryBtn onClick={next} loading={leaving}>Continue</PrimaryBtn></Actions>
      </>
    );
  }
  return (
    <>
      <TitleBlock eyebrow="Team" title="This looks like an earlier profile of yours" sub="We found a profile with your name from before you signed up. If it is yours, tell us and an admin will review it. Nothing changes until they do." />
      <div className="flex flex-col gap-[10px]">
        {candidates.map((c) => (
          <div key={c.candidateId} className="flex items-center gap-3 rounded-[12px] border border-bm-border bg-white p-4">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-bm-subtle bm-label-xs text-bm-muted">{initialsOf(...String(c.name || "").trim().split(" "))}</span>
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <div className="truncate bm-label-l text-bm-ink">{c.name || "Athlete"}</div>
              <div className="truncate bm-body-s text-bm-muted">{[c.team, c.gradYear ? `Class of ${c.gradYear}` : ""].filter(Boolean).join(" · ") || "No team on file"}</div>
            </div>
            {c.requested
              ? <OutlineBadge>Sent for review</OutlineBadge>
              : <SecondaryBtn className="!h-10 !w-auto shrink-0 !px-4" onClick={() => claim(c.candidateId)} disabled={!!busy || leaving}>{busy === c.candidateId ? "Sending..." : "This is me"}</SecondaryBtn>}
          </div>
        ))}
      </div>
      {error && <Note tone="critical">{error}</Note>}
      <Actions><TextBtn onClick={next} disabled={leaving || !!busy}>Not me</TextBtn></Actions>
    </>
  );
};
